/* eslint-disable consistent-return */
import get from 'lodash/get'
import map from 'lodash/map'
import size from 'lodash/size'
import isPlainObject from 'lodash/isPlainObject'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import { fromJS } from 'immutable'
import { get as getConfig } from '../../config/index'
import strictUriEncode from '../../strict-uri-encode/index'
import { formatTracking } from '../checkout/index'
import {
  get as apiGet, post as apiPost, del as apiDel, TYPE_BROOKLYN_JSON,
} from '../../api-client/index'
import { isDefault } from '../languages/index'

// **************************
// NEW SHARE FUNCTIONALITY
// Including Friends Watch Free
// **************************

export const SHARE_LINK_OID_PARAM = 244

export const SHARE_LINKS = {
  FACEBOOK: 'https://www.facebook.com/sharer/sharer.php?u={url}',
  TWITTER: 'https://twitter.com/intent/tweet?url={url}&text={title}&via={user_id}',
  REDDIT: 'https://reddit.com/submit?url={url}&title={title}',
  // eslint-disable-next-line no-template-curly-in-string
  EMAIL: 'mailto:username@example.com?subject=Checkout {title} on Gaia&body={url}',
  GAIA_USER: 'YourMotherGaia',
}

export const UNAVAILABLE_REASON = {
  TIME_LIMIT_EXCEEDED: 'TIME_LIMIT_EXCEEDED',
  VIEW_LIMIT_EXCEEDED: 'VIEW_LIMIT_EXCEEDED',
  NOT_SHARABLE: 'NOT_SHARABLE',
  GEO_NOT_AVAILABLE: 'GEO_NOT_AVAILABLE',
  EMAIL_VIEW_LIMIT_EXCEEDED: 'EMAIL_VIEW_LIMIT_EXCEEDED',
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
  INVALID_EMAIL: 'INVALID_EMAIL',
}

const config = getConfig()

function handleSuccess (res) {
  const data = get(res, 'body', {})
  const errors = get(data, 'errors', [])
  return fromJS({ data, success: size(errors) < 1 })
}

function handleError (data) {
  return fromJS({
    data: isPlainObject(data) ? data : {},
    success: false,
  })
}

export function copyToClipboard (what) {
  //
  const textProvided = typeof what === 'string'
  const isValidInput = what && /input|textarea/i.test(what.tagName)

  if (!textProvided && !isValidInput) {
    throw new Error(`copyToClipboard() :: Cannot copy ${what}.`)
  }

  let input
  let value
  let success
  // if text was provided,
  // we need to create a temporary element
  // to put our text in
  if (textProvided) {
    // creating an element to allow a clipboard copy without triggering a react render
    input = document.createElement('input')
    input.style.position = 'fixed'
    input.style.opacity = '0'
    input.value = what
    input.type = 'text'
    value = what
  } else {
    input = what
    value = what.value
  }
  // iOS will not let you copy
  // text from readOnly inputs SO,
  // we have to do this nonsense
  const { readOnly } = input

  if (readOnly) {
    input.readOnly = false
  }

  if (textProvided) {
    document.body.appendChild(input)
  }
  // wrapped in try/catch for browsers that do not
  // support createRange, Selection API, or execCommand
  try {
    const range = document.createRange()
    const selection = window.getSelection()

    range.selectNodeContents(input)
    selection.removeAllRanges()
    selection.addRange(range)

    input.focus()
    input.setSelectionRange(0, value.length)
    document.execCommand('copy')
    success = true
  } catch (err) {
    success = false
  }

  if (textProvided) {
    document.body.removeChild(input)
  }

  if (readOnly) {
    input.readOnly = true
  }
  return success
}

export async function getShare (options = {}) {
  const { token, language, auth } = options
  try {
    const result = await apiGet(`/v2/shares/${strictUriEncode(token)}`, { language }, { auth }, TYPE_BROOKLYN_JSON)
    return handleSuccess(result)
  } catch (e) {
    return handleError()
  }
}

export async function redeemShare (options = {}) {
  const {
    token,
    language,
    hashedEmail,
    auth,
    country,
  } = options

  try {
    const result = await apiPost('/v3/shares/redeem-share', {
      token,
      language,
      hashedEmail,
    }, {
      auth,
      headers: _omitBy({
        'cloudfront-viewer-country': country,
      }, _isUndefined),
    }, TYPE_BROOKLYN_JSON)
    const data = get(result, 'body', {})
    const success = get(data, 'success')
    return fromJS({ data, success })
  } catch (e) {
    return handleError()
  }
}

export function getUserShares (options = {}) {
  const { auth } = options
  return apiGet('shares/user', null, { auth }, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function getHasShared (options = {}) {
  const { auth } = options
  return apiGet('shares/user-has-shared', null, { auth }, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function getAccountShares (options = {}) {
  const { auth } = options
  return apiGet('shares/account', null, { auth }, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function createShare (options = {}) {
  const {
    auth, type, contentId, source, personalization,
  } = options
  return apiPost('/shares', {
    type, contentId, source, personalization,
  }, { auth }, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export async function duplicateShareEmailCapture (options = {}) {
  const {
    token, email, language, inboundTracking,
  } = options

  const tracking = formatTracking(inboundTracking)

  try {
    const result = await apiPost('/shares/email-capture', {
      token,
      email,
      language,
      tracking,
    }, null, TYPE_BROOKLYN_JSON)
    return handleSuccess(result)
  } catch (e) {
    return handleError(e)
  }
}

export function setShareSource (options = {}) {
  const { auth, token, source } = options
  return apiPost('/shares/source', { token, source }, { auth }, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function setConversion (options = {}) {
  const { token } = options
  return apiPost(`/shares/${strictUriEncode(token)}/conversion`, null, null, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function setQualifiedView (options = {}) {
  const { token } = options
  return apiPost(`/shares/${strictUriEncode(token)}/qualified-view`, null, null, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function addPersonalization (options = {}) {
  const { token, personalization } = options
  return apiPost(`/shares/${strictUriEncode(token)}/personalize`, { personalization }, null, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function removePersonalization (options = {}) {
  const { token } = options
  return apiDel(`/shares/${strictUriEncode(token)}/personalize`, null, null, TYPE_BROOKLYN_JSON)
    .then(handleSuccess)
    .catch(handleError)
}

export function getShareV3Link (props) {
  const {
    userLang,
    userAffId,
    userRfd,
    userUid,
    contentUrl,
    contentId,
    contentType,
  } = props

  const path = isDefault(userLang) ? `/share/${contentUrl}` : `/${userLang}/share/${contentUrl}`

  let baseQuery = `?type=${contentType}&contentId=${contentId}`
  if (userAffId) {
    baseQuery += `&affid=${userAffId}&oid=${SHARE_LINK_OID_PARAM}`
  } else if (userRfd) {
    baseQuery += `&rfd=${userRfd}`
  }
  if (userUid) {
    baseQuery += `&uuid=${userUid}`
  }

  return `${path}${baseQuery}&source=SHARE`
}

// **************************
// OLD SHARE FUNCTIONALITY
// DO NOT USE
// **************************

export const BASE_FACEBOOK_SHARE = 'https://www.facebook.com/sharer/sharer.php'
export const BASE_TWITTER_SHARE = 'https://twitter.com/home'

const WINDOW_NAME = 'share-window'

export function getFacebookHref (path, absolute = false) {
  const url = getShareUrl(path, absolute)

  return `${BASE_FACEBOOK_SHARE}?u=${encodeURIComponent(url)}`
}

export function getTwitterHref (path, title, prefix, suffix, absolute = false) {
  const url = getShareUrl(path, absolute)
  const message = `${prefix} ${title} ${suffix} ${url}`

  return `${BASE_TWITTER_SHARE}?status=${encodeURIComponent(message)}`
}

function getShareUrl (path, absolute = false) {
  if (absolute) {
    return path
  }
  return `${config.origin}/${path}`
}

export function openWindow (e, openShareWindow, url, width = 540, height = 250) {
  if (!openShareWindow) {
    return
  }

  if (e !== null) {
    e.preventDefault()
  }

  const windowParams = {
    height,
    width,
    top: window.screenTop + 50,
    left: window.screenLeft + 50,
  }

  const paramsString = map(windowParams, (
    value,
    param,
  ) => `${param}=${value}`).join(',')

  window.open(url, WINDOW_NAME, paramsString)
}
